import React from "react";
import InputLabel from "./InputLabel";
import ErrorMessage from "../ErrorMessages/ErrorMessage";
import { getMaxLength } from "../commonfunctions/getMaxLength";
import useTranslation from "../CustomHooks/useTranslation";
import { inputFieldContent } from "./InputFieldContent";
import InputShimmer from "./InputShimmer";

interface InputItem {
  labelName: string;
  id: string;
}

interface CurrencyInputProps {
  inputItem: InputItem;
  errorMessage?: string;
  required?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onValueChange?: (id: string, value: string) => void; // Added for direct value passing
  fieldError?: string | boolean;
  tabIndex: number;
  maxLength?: number;
  testId?: string;
  disabled?: boolean;
  value?: string;
  className?: string;
  maindivstyle?: string;
  editLoading?: boolean;
  placeholder?: string;
  secondaryText?: string;
  customWidth?: string;
  currency?: string;
  min?: string | number; // Added min prop
  max?: string | number; // Added max prop
}

const CurrencyInputField: React.FC<CurrencyInputProps> = ({
  inputItem,
  errorMessage,
  onChange,
  onValueChange,
  required,
  fieldError,
  maxLength,
  testId,
  disabled,
  value,
  className,
  maindivstyle,
  editLoading,
  placeholder,
  secondaryText,
  customWidth,
  currency = "AFA",
  min = "1", // Default minimum value set to 1
  max = "1000000000000", // Default maximum value set to 1 billion
}) => {
  const { labelName, id } = inputItem;
  const { translate, notEnglish } = useTranslation(inputFieldContent);

  const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;

  // Handle change event with numeric validation
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let { value } = event.target;

    // Empty value handling
    if (value === '') {
      if (onChange) {
        onChange(event);
      }
      if (onValueChange) {
        onValueChange(id, value);
      }
      return;
    }

    // Allow digits, one decimal point, and handle leading decimal
    if (value === '.') {
      value = '0.';
    } else if (!/^\d*\.?\d*$/.test(value)) {
      return;
    }

    // Validate format: max 12 digits + 2 decimal places
    const parts = value.split('.');

    // Restrict whole number part to 12 digits
    if (parts[0] && parts[0].length > 12) {
      value = `${parts[0].slice(0, 12)}${parts[1] ? `.${parts[1]}` : ''}`;
    }

    // Restrict decimal part to 2 digits
    if (parts[1] && parts[1].length > 2) {
      value = `${parts[0]}.${parts[1].slice(0, 2)}`;
    }

    // Update the input field value
    event.target.value = value;

    // Call the original onChange handler if provided
    if (onChange) {
      onChange(event);
    }

    // Call the value-specific onChange if provided
    if (onValueChange) {
      onValueChange(id, value);
    }
  };


  return (
    <div className={`${maindivstyle}`}>
      <InputLabel labelName={labelName} id={id} required={required} />
      {editLoading ? (
        <InputShimmer />
      ) : (
        <div className="relative">
          <div
            className={`flex items-center bg-white ${customWidth ? customWidth : 'w-80'} h-14 border rounded-lg ${errorMessage || fieldError ? "border-negative" : "border-P_light_active"
              } ${disabled ? "bg-disable_fill border-disable_stroke" : ""} focus-within:border-2`}
          >
            {!notEnglish && (
              <div className={`flex items-center pl-2.5 focus:pl-[9px] ${isFirefox ? '' : 'pt-[2.5px]'} text-subTitle ${disabled ? "bg-disable_fill text-disable_text h-full rounded-l-lg" : "bg-white"}`}>
                {currency}
              </div>
            )}
            <input
              id={id}
              data-testid={testId || id}
              className={`${className} ${customWidth} flex-1 h-full py-5 pr-[15px] pl-2.5 focus:pl-[9px] disabled:cursor-not-allowed 
                disabled:bg-disable_fill text-subTitle outline-hidden rounded-r-lg ${disabled ? "bg-disable_fill" : "bg-white rounded-lg"
                }`}
              placeholder={placeholder ? translate(placeholder) : translate(`Enter amount`)}
              onChange={handleChange}
              // onBlur={handleBlur}
              autoComplete="off"
              maxLength={maxLength || 15} // 12 digits + decimal point + 2 decimal places
              disabled={disabled}
              value={value}
              type="text"
              pattern="\d*\.?\d*"
              min={min}
              max={max}
            />
            {notEnglish && (
              <div className={`flex items-center pl-3 ${isFirefox ? '' : 'pt-[2.5px]'} text-subTitle ${disabled ? "bg-disable_fill text-disable_text" : "bg-white"}`}>
                {currency}
              </div>
            )}
          </div>
          {secondaryText && <p className="mt-1 text-body2 text-secondary_text">{secondaryText}</p>}
          {errorMessage && !disabled && (
            <div className="mb-1 absolute">
              <ErrorMessage
                errorMessage={errorMessage}
                testId={`error-${testId || id}`}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CurrencyInputField;